import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { usePermissions } from '../../contexts/PermissionContext';
import { canAccessPath } from '../../config/routePermissions';
import OnShiftBar from './OnShiftBar';
import NextShiftCard from './NextShiftCard';
import MySchedulePanel from './MySchedulePanel';
import WorkDayTasks from './WorkDayTasks';
import { formatMonthParam, getCurrentMonth } from './workDayUtils';
import './WorkDayDashboard.css';

/**
 * WorkDayDashboard - "Moj radni dan" za negovatelja, medicinskog tehničara
 * (sestra / negovateljica) i fizioterapeuta.
 *
 * Učitava sopstvenu smenu (trenutna + naredna) i mesečni raspored smena, a
 * zadatke preuzima/pregleda preko `tasksEndpoint` (u zavisnosti od role).
 */
const WorkDayDashboard = ({ tasksEndpoint = '/api/Task/caregiver-tasks' }) => {
  const navigate = useNavigate();
  const { canAny, permissions, isAdmin } = usePermissions();

  const [shift, setShift] = useState({ data: null, loading: true, error: null });
  const [shifts, setShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(true);
  const [shiftMonth, setShiftMonth] = useState(getCurrentMonth);

  // Trenutna i naredna smena
  useEffect(() => {
    let isMounted = true;

    const fetchShift = async () => {
      try {
        setShift({ data: null, loading: true, error: null });
        const response = await axiosInstance.get('/api/Shift/my-current-and-next-shift');
        const body = response.data;
        const payload = body && body.data ? body.data : body;
        if (isMounted) setShift({ data: payload || null, loading: false, error: null });
      } catch (err) {
        // 404 (nema smene za danas) nije greška - prikaži prazno stanje
        if (err?.response?.status === 404) {
          if (isMounted) setShift({ data: null, loading: false, error: null });
        } else {
          console.error('Greška pri učitavanju smene:', err);
          if (isMounted) setShift({ data: null, loading: false, error: 'Greška pri učitavanju smene' });
        }
      }
    };

    fetchShift();
    return () => {
      isMounted = false;
    };
  }, []);

  // Mesečni raspored smena za izabrani mesec (parametar month=MM-yyyy)
  useEffect(() => {
    const controller = new AbortController();

    const fetchMyShifts = async () => {
      try {
        setShiftsLoading(true);
        const response = await axiosInstance.get('/api/Shift/my-shifts', {
          params: { month: formatMonthParam(shiftMonth) },
          signal: controller.signal,
        });
        const body = response.data;
        const payload = body && body.data ? body.data : body;
        const list = Array.isArray(payload?.shifts) ? payload.shifts : [];
        if (!controller.signal.aborted) setShifts(list);
      } catch (err) {
        // Prekinut zahtev (brza promena meseca) nije greška
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        console.error('Greška pri učitavanju smena:', err);
        if (!controller.signal.aborted) setShifts([]);
      } finally {
        if (!controller.signal.aborted) setShiftsLoading(false);
      }
    };

    fetchMyShifts();
    return () => {
      controller.abort();
    };
  }, [shiftMonth]);

  const todayShiftRaw = shift.data?.todayShift || null;
  // Prazan objekat (npr. {}) tretiramo kao "nema smene"
  const todayShift =
    todayShiftRaw && (todayShiftRaw.startTime || todayShiftRaw.endTime) ? todayShiftRaw : null;
  const nextShift = shift.data?.nextShift || null;

  // "Detalji smene" vodi na stranicu smena samo ako korisnik ima dozvolu
  const canOpenShiftManagement = canAccessPath('/shift-management', { canAny, permissions, isAdmin });
  const handleOpenShiftDetails = canOpenShiftManagement
    ? () => navigate('/shift-management')
    : undefined;

  return (
    <div className="wdash-workspace">
      <div className="wdash-page">
        <div className="wdash-grid">
          <div className="wdash-col wdash-col--main">
            <OnShiftBar
              todayShift={todayShift}
              nextShift={nextShift}
              loading={shift.loading}
              onOpenResident={(residentId) => navigate(`/residents/${residentId}`)}
            />
            <WorkDayTasks endpoint={tasksEndpoint} />
          </div>

          <div className="wdash-col wdash-col--side">
            <NextShiftCard
              nextShift={nextShift}
              loading={shift.loading}
              onOpenDetails={handleOpenShiftDetails}
            />
            <MySchedulePanel
              shifts={shifts}
              loading={shiftsLoading}
              year={shiftMonth.year}
              month={shiftMonth.month}
              onMonthChange={setShiftMonth}
              todayShift={todayShift}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkDayDashboard;
